import styled from "@emotion/styled";
import { useCallback } from "react";

import { Cell, FONT_WEIGHT_SEMI_BOLD, Icon, TableRow } from "@skilitics/designer-ui-components";
import { FormattedMessage, useFormatMessage, type FormatMessages } from "@skilitics/localization";

import { createFolderFilter } from "../../../filters";
import { useLandingPageSettings } from "../../../LandingPageSettingsContext";
import { columns, type Column } from "./columns";
import { Description, DetailsContainer, IconContainer, IconWrapper, Label, Thumbnail, Title } from "./ListItemElements";

const SearchCount = styled.span`
  font-weight: ${FONT_WEIGHT_SEMI_BOLD};
`;

type Props = {
  title: string;
  filter: string;
  moduleCount: number;
  searchCount?: number;
};

const getColumnAlignment = (id: Column["id"]) => columns.find((column) => column.id === id)?.align;

export const Folder = ({ title, filter, moduleCount, searchCount }: Props) => {
  const formatMessage = useFormatMessage(formatStrings);

  const { addFilterSet } = useLandingPageSettings();

  const handleClick = useCallback(
    () => addFilterSet({ id: "folder", filters: [createFolderFilter(filter)], operator: "and", metadata: filter }),
    [addFilterSet, filter],
  );

  return (
    <TableRow onClick={handleClick} data-test="list-folder-container">
      <Cell align={getColumnAlignment("thumbnail")}>
        <Thumbnail>
          <img src="/assets/warehouse.jpg" alt={formatMessage("folder")} />
          <IconContainer>
            <IconWrapper $show={!!searchCount}>
              <Icon icon="search" />
              <SearchCount>{searchCount}</SearchCount>
            </IconWrapper>
          </IconContainer>
        </Thumbnail>
      </Cell>
      <Cell align={getColumnAlignment("titleAndDescription")}>
        <DetailsContainer>
          <Title>{title}</Title>
          <Description>
            <FormattedMessage messages={formatStrings.contains} values={{ moduleCount }} />
          </Description>
        </DetailsContainer>
      </Cell>
      <Cell align={getColumnAlignment("tags")} />
      <Cell align={getColumnAlignment("modified")}>
        <Label>{""}</Label>
      </Cell>
      <Cell align={getColumnAlignment("lock")}>{""}</Cell>
    </TableRow>
  );
};

const formatStrings = {
  contains: {
    en: "Folder contains {moduleCount, plural, =1 {# module} other {# modules}}.",
  },
  folder: {
    en: "Folder",
  },
} satisfies FormatMessages;
